import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { ArrowLeft, Crop, Crown, Grid3X3, ImagePlus, LayoutGrid, LoaderCircle, Play, RefreshCw, RotateCcw, ScanLine, Settings2, ShieldCheck, Sparkles, Upload } from 'lucide-react';
import type { BackgroundMode, BlankCanvasOptions, ColorMode, FitMode, ImageStyle, ProcessOptions, SampleMode } from '../types';
import { DimensionInput } from './DimensionInput';
import { ImageCropModal } from './ImageCropModal';

const presets = [52, 78, 104, 156];

function readAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('无法读取背景图'));
    reader.readAsDataURL(file);
  });
}

export function SetupPanel({ generating, maxCanvasSize, allowBlankCanvas, initialSource, onGenerate, onRecognize, onCreateCanvas, onRequirePro, onCancelAdjustment }: {
  generating: boolean;
  maxCanvasSize: number;
  allowBlankCanvas: boolean;
  initialSource?: { dataUrl: string; name: string; options: ProcessOptions };
  onGenerate: (file: File, options: ProcessOptions) => void;
  onRecognize: (file: File) => void;
  onCreateCanvas: (options: BlankCanvasOptions) => void;
  onRequirePro: (feature: string) => void;
  onCancelAdjustment?: () => void;
}) {
  const [mode, setMode] = useState<'image' | 'pattern' | 'blank'>('image');
  const [file, setFile] = useState<File | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [cropped, setCropped] = useState(false);
  const [preview, setPreview] = useState(initialSource?.dataUrl || '');
  const [pendingCrop, setPendingCrop] = useState<File | null>(null);
  const [pendingBackgroundCrop, setPendingBackgroundCrop] = useState<File | null>(null);
  const [width, setWidth] = useState(initialSource?.options.width ?? 52);
  const [height, setHeight] = useState(initialSource?.options.height ?? 52);
  const [fitMode, setFitMode] = useState<FitMode>(initialSource?.options.fitMode ?? 'cover');
  const [colorMode, setColorMode] = useState<ColorMode>(initialSource?.options.colorMode ?? 'accurate');
  const [backgroundMode, setBackgroundMode] = useState<BackgroundMode>(initialSource?.options.backgroundMode ?? (initialSource?.options.subjectMode ? 'remove' : 'auto'));
  const [maxColors, setMaxColors] = useState(initialSource?.options.maxColors ?? (initialSource?.options.colorMode === 'limited' ? 12 : initialSource?.options.colorMode === 'normal' ? 32 : 48));
  const [sampleMode, setSampleMode] = useState<SampleMode>(initialSource?.options.sampleMode ?? 'auto');
  const [imageStyle, setImageStyle] = useState<ImageStyle>(initialSource?.options.imageStyle ?? 'natural');
  const [styleStrength, setStyleStrength] = useState(initialSource?.options.styleStrength ?? 60);
  const [detailBoost, setDetailBoost] = useState(initialSource?.options.detailBoost ?? (initialSource?.options.colorMode === 'normal' ? 15 : initialSource?.options.colorMode === 'limited' ? 10 : 25));
  const [mergeRareColors, setMergeRareColors] = useState(initialSource?.options.mergeRareColors ?? (initialSource?.options.colorMode === 'normal' ? 2 : initialSource?.options.colorMode === 'limited' ? 1 : 0));
  const [cleanupIslands, setCleanupIslands] = useState(initialSource?.options.cleanupIslands ?? (initialSource?.options.colorMode === 'normal' ? 1 : initialSource?.options.colorMode === 'limited' ? 2 : 0));
  const [dithering, setDithering] = useState(initialSource?.options.dithering ?? false);
  const [advancedColorOpen, setAdvancedColorOpen] = useState(false);
  const [canvasName, setCanvasName] = useState('未命名画布');
  const [backgroundImage, setBackgroundImage] = useState('');
  const [backgroundName, setBackgroundName] = useState('');
  const [backgroundOpacity, setBackgroundOpacity] = useState(0.35);
  const input = useRef<HTMLInputElement>(null);
  const patternInput = useRef<HTMLInputElement>(null);
  const backgroundInput = useRef<HTMLInputElement>(null);
  const selectedPreset = width === height && presets.includes(width) ? width : 0;

  function applyColorPreset(mode: ColorMode) {
    setColorMode(mode);
    setDithering(false);
    setSampleMode('auto');
    if (mode === 'accurate') {
      setMaxColors(48);
      setDetailBoost(25);
      setMergeRareColors(0);
      setCleanupIslands(0);
    } else if (mode === 'normal') {
      setMaxColors(32);
      setDetailBoost(20);
      setMergeRareColors(2);
      setCleanupIslands(1);
    } else {
      setMaxColors(12);
      setDetailBoost(15);
      setMergeRareColors(1);
      setCleanupIslands(2);
    }
  }

  useEffect(() => {
    setWidth((value) => Math.min(value, maxCanvasSize));
    setHeight((value) => Math.min(value, maxCanvasSize));
    if (!allowBlankCanvas) setMode('image');
  }, [allowBlankCanvas, maxCanvasSize]);

  useEffect(() => {
    if (!initialSource) return;
    let active = true;
    fetch(initialSource.dataUrl)
      .then((response) => response.blob())
      .then((blob) => {
        if (!active) return;
        const next = new File([blob], initialSource.name, { type: blob.type || 'image/png' });
        setFile(next);
        setSourceFile(next);
      })
      .catch(() => { if (active) setFile(null); });
    return () => { active = false; };
  }, [initialSource?.dataUrl, initialSource?.name]);

  const fileSize = useMemo(() => file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : '', [file]);

  function finishChoose(next: File, isCropped = false) {
    if (preview) URL.revokeObjectURL(preview);
    setFile(next);
    setPreview(URL.createObjectURL(next));
    setCropped(isCropped);
    setPendingCrop(null);
  }

  function chooseFile(next: File | null) {
    if (!next || !next.type.startsWith('image/')) return;
    setSourceFile(next);
    finishChoose(next, false);
  }

  function chooseBackground(next: File | null) {
    if (!next || !next.type.startsWith('image/')) return;
    setPendingBackgroundCrop(next);
  }

  async function finishBackground(next: File) {
    setBackgroundImage(await readAsDataUrl(next));
    setBackgroundName(next.name);
    setPendingBackgroundCrop(null);
    if (backgroundInput.current) backgroundInput.current.value = '';
  }

  function drop(event: DragEvent) {
    event.preventDefault();
    const next = event.dataTransfer.files[0];
    if (!next?.type.startsWith('image/')) return;
    if (mode === 'pattern') onRecognize(next);
    else chooseFile(next);
  }

  async function loadExample() {
    const canvas = document.createElement('canvas');
    canvas.width = 780;
    canvas.height = 780;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#fffaf2';
    ctx.fillRect(0, 0, 780, 780);
    const circle = (x: number, y: number, radius: number, color: string) => {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    };
    circle(390, 370, 220, '#f7c9c3');
    circle(300, 300, 88, '#f4675c');
    circle(480, 300, 88, '#f2c14e');
    circle(300, 475, 88, '#58b09c');
    circle(480, 475, 88, '#7c5cff');
    circle(390, 390, 98, '#fff4a8');
    circle(355, 370, 13, '#292522');
    circle(425, 370, 13, '#292522');
    ctx.beginPath();
    ctx.moveTo(350, 425);
    ctx.quadraticCurveTo(390, 458, 430, 425);
    ctx.strokeStyle = '#292522';
    ctx.lineWidth = 14;
    ctx.lineCap = 'round';
    ctx.stroke();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (blob) chooseFile(new File([blob], '示例笑脸花朵.png', { type: 'image/png' }));
  }

  const sizeControls = (
    <fieldset>
      <legend>底板尺寸（当前最大 {maxCanvasSize}）</legend>
      <div className="preset-grid">
        {presets.filter((size) => size <= maxCanvasSize).map((size) => <button key={size} className={selectedPreset === size ? 'active' : ''} type="button" onClick={() => { setWidth(size); setHeight(size); }}>{size}<small>× {size}</small></button>)}
      </div>
      <div className="dimension-row">
        <DimensionInput label="宽" value={width} max={maxCanvasSize} onCommit={setWidth} />
        <span>×</span>
        <DimensionInput label="高" value={height} max={maxCanvasSize} onCommit={setHeight} />
      </div>
      <div className="palette-identity"><strong>MARD</strong><span>当前转换色卡：MARD 291</span><small>COCO 等品牌使用独立编号，不混用</small></div>
    </fieldset>
  );

  return (
    <div className="setup-panel">
      {initialSource ? <div className="adjustment-banner">
        <button type="button" onClick={onCancelAdjustment} aria-label="返回当前图纸"><ArrowLeft size={18} /></button>
        <span><strong><RefreshCw size={15} />调整现有图纸</strong><small>保留项目名称，重新生成会清空原施工进度</small></span>
      </div> : <div className="setup-tabs" aria-label="新建方式">
        <button className={mode === 'image' ? 'active' : ''} type="button" onClick={() => setMode('image')}><ImagePlus size={17} />图片转图纸</button>
        <button className={mode === 'pattern' ? 'active' : ''} type="button" onClick={() => setMode('pattern')}><ScanLine size={17} />识别现成图纸</button>
        <button className={`${mode === 'blank' ? 'active' : ''} ${allowBlankCanvas ? '' : 'feature-locked'}`} type="button" onClick={() => allowBlankCanvas ? setMode('blank') : onRequirePro('新建空白画布')}><LayoutGrid size={17} />新建画布{!allowBlankCanvas && <small><Crown size={12} />PRO</small>}</button>
      </div>}

      {mode === 'image' ? <>
        <div className="panel-heading">
          <div><p className="eyebrow">{initialSource ? '转换调整' : '准备图纸'}</p><h2>{initialSource ? '重新设置生成参数' : '选一张图片'}</h2></div>
          <Sparkles size={22} />
        </div>
        <button className={`upload-zone ${preview ? 'has-image' : ''}`} type="button" onClick={() => input.current?.click()} onDrop={drop} onDragOver={(event) => event.preventDefault()}>
          {preview ? <img src={preview} alt="待转换图片预览" /> : <ImagePlus size={32} />}
          <span className="upload-copy"><strong>{file ? file.name : '选择或拖入图片'}</strong><small>{file ? `${fileSize} · ${cropped ? '使用裁切区域' : '当前保留完整原图'}` : '选择后可按需裁切 · 图片仅在本机处理'}</small></span>
          <Upload size={19} />
        </button>
        <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(event) => chooseFile(event.target.files?.[0] || null)} />
        {file && <div className="image-prepare-actions">
          <button type="button" onClick={() => setPendingCrop(sourceFile || file)}><Crop size={15} />裁切图片</button>
          {cropped && sourceFile && <button type="button" onClick={() => finishChoose(sourceFile, false)}><RotateCcw size={15} />恢复完整原图</button>}
          <span>裁切是可选步骤，图片适配会在生成时执行。</span>
        </div>}
        <button className="sample-button" type="button" onClick={loadExample}><Play size={14} />手边没图片？用示例试试</button>
        {sizeControls}
        <fieldset>
          <legend>图片适配</legend>
          <div className="segmented-control">
            <button className={fitMode === 'cover' ? 'active' : ''} type="button" onClick={() => setFitMode('cover')}><strong>填满底板</strong><small>可能裁掉部分边缘</small></button>
            <button className={fitMode === 'contain' ? 'active' : ''} type="button" onClick={() => setFitMode('contain')}><strong>完整显示</strong><small>两侧可能留空</small></button>
          </div>
        </fieldset>
        <fieldset>
          <legend>风格处理</legend>
          <div className="style-treatment-grid">
            {([
              ['natural', '原图保真', '不改色调，适合浅色插画'],
              ['soft', '柔和色块', '减少杂色和细碎渐变'],
              ['vivid', '鲜明色块', '增强颜色和画面对比'],
              ['outline', '清晰描边', '强调人物与物体轮廓']
            ] as Array<[ImageStyle, string, string]>).map(([value, label, description]) => <button key={value} className={imageStyle === value ? 'active' : ''} type="button" onClick={() => setImageStyle(value)}><strong>{label}</strong><small>{description}</small></button>)}
          </div>
          {imageStyle !== 'natural' && <label className="style-strength"><span><strong>处理强度</strong><small>风格处理发生在色号匹配之前</small></span><input type="range" min="20" max="100" step="5" value={styleStrength} onChange={(event) => setStyleStrength(Number(event.target.value))} /><b>{styleStrength}%</b></label>}
        </fieldset>
        <fieldset>
          <legend>色彩方式</legend>
          <div className="stacked-options">
            <button className={colorMode === 'accurate' ? 'active' : ''} type="button" onClick={() => applyColorPreset('accurate')}><span>原图优先</span><small>48 色起步，保留浅色、轮廓和局部颜色</small></button>
            <button className={colorMode === 'normal' ? 'active' : ''} type="button" onClick={() => applyColorPreset('normal')}><span>均衡生成</span><small>32 色，适度合并零碎色块</small></button>
            <button className={colorMode === 'limited' ? 'active' : ''} type="button" onClick={() => applyColorPreset('limited')}><span>少色模式</span><small>12 色，优先减少备料种类</small></button>
          </div>
          <button className={`advanced-toggle ${advancedColorOpen ? 'active' : ''}`} type="button" aria-expanded={advancedColorOpen} onClick={() => setAdvancedColorOpen((value) => !value)}><Settings2 size={15} />{advancedColorOpen ? '收起高级参数' : '展开高级参数'}<span>当前最多 {maxColors} 色</span></button>
          {advancedColorOpen && <div className="conversion-advanced">
            <label><span><strong>最大用色数</strong><small>限制需要准备的色号数量</small></span><input type="range" min="4" max="80" step="1" value={maxColors} onChange={(event) => { setMaxColors(Number(event.target.value)); if (colorMode === 'limited' && Number(event.target.value) !== 12) setColorMode('accurate'); }} /><b>{maxColors}</b></label>
            <label><span><strong>取样方式</strong><small>插画选主体取样，照片选平滑取样</small></span><select value={sampleMode} onChange={(event) => setSampleMode(event.target.value as SampleMode)}><option value="auto">自适应保真</option><option value="dominant">主体色取样</option><option value="smooth">平滑取样</option></select></label>
            <label><span><strong>细节增强</strong><small>提高轮廓和五官辨识度，过高会变硬</small></span><input type="range" min="0" max="70" step="5" value={detailBoost} onChange={(event) => setDetailBoost(Number(event.target.value))} /><b>{detailBoost}%</b></label>
            <label><span><strong>合并少量色号</strong><small>从用量最少的颜色开始合并</small></span><input type="range" min="0" max="12" step="1" value={mergeRareColors} onChange={(event) => setMergeRareColors(Number(event.target.value))} /><b>{mergeRareColors}</b></label>
            <label><span><strong>清理孤立色块</strong><small>合并不超过指定格数的小区域</small></span><input type="range" min="0" max="8" step="1" value={cleanupIslands} onChange={(event) => setCleanupIslands(Number(event.target.value))} /><b>{cleanupIslands}</b></label>
            <label className="advanced-switch"><span><strong>Floyd-Steinberg 抖动</strong><small>让渐变更平滑，但会增加杂点</small></span><input type="checkbox" checked={dithering} onChange={(event) => setDithering(event.target.checked)} /></label>
          </div>}
        </fieldset>
        <fieldset>
          <legend>背景识别</legend>
          <div className="background-mode-control">
            <button className={backgroundMode === 'auto' ? 'active' : ''} type="button" onClick={() => setBackgroundMode('auto')}><strong>自动识别</strong><small>透明背景直接留空，纯色边缘自动移除</small></button>
            <button className={backgroundMode === 'keep' ? 'active' : ''} type="button" onClick={() => setBackgroundMode('keep')}><strong>保留背景</strong><small>适合风景、场景和满版图片</small></button>
            <button className={backgroundMode === 'remove' ? 'active' : ''} type="button" onClick={() => setBackgroundMode('remove')}><strong>强制去边缘</strong><small>从四周向内移除相近背景色</small></button>
          </div>
        </fieldset>
        <button className="primary-button generate-button" type="button" disabled={!file || generating} onClick={() => file && onGenerate(file, { width, height, fitMode, colorMode, subjectMode: backgroundMode === 'remove', backgroundMode, maxColors, sampleMode, imageStyle, styleStrength, detailBoost, mergeRareColors, cleanupIslands, dithering })}>
          {generating ? <LoaderCircle className="spin" size={20} /> : <Sparkles size={20} />}
          <span>{generating ? '正在本地生成…' : initialSource ? '重新生成并覆盖图纸' : '生成拼豆图纸'}</span>
        </button>
      </> : mode === 'pattern' ? <>
        <div className="panel-heading">
          <div><p className="eyebrow">还原网上图纸</p><h2>识别网格和色号</h2></div>
          <ScanLine size={22} />
        </div>
        <button className="upload-zone pattern-upload-zone" type="button" onClick={() => patternInput.current?.click()} onDrop={drop} onDragOver={(event) => event.preventDefault()}>
          <Grid3X3 size={32} />
          <span className="upload-copy"><strong>选择或拖入成品图纸</strong><small>适合带网格、色号和色卡的现成拼豆图</small></span>
          <Upload size={19} />
        </button>
        <input ref={patternInput} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(event) => { const next = event.target.files?.[0]; if (next) onRecognize(next); event.currentTarget.value = ''; }} />
        <div className="pattern-recognition-notes">
          <span><strong>1</strong>对齐最外侧网格</span>
          <span><strong>2</strong>确认行列数量</span>
          <span><strong>3</strong>批量校对色号</span>
        </div>
        <p className="pattern-recognition-help">首版支持正面、规整的 MARD 网格图纸。坐标、边框和底部色卡不要框进网格区域。</p>
      </> : <>
        <div className="panel-heading">
          <div><p className="eyebrow">从零开始</p><h2>新建空白画布</h2></div>
          <LayoutGrid size={22} />
        </div>
        <label className="canvas-name-field">画布名称<input maxLength={40} value={canvasName} onChange={(event) => setCanvasName(event.target.value)} /></label>
        {sizeControls}
        <fieldset>
          <legend>参考底图（可选）</legend>
          <button className={`background-picker ${backgroundImage ? 'has-image' : ''}`} type="button" onClick={() => backgroundInput.current?.click()}>
            {backgroundImage ? <img src={backgroundImage} alt="参考底图预览" /> : <ImagePlus size={25} />}
            <span><strong>{backgroundName || '导入一张参考图'}</strong><small>只作描图参考，不会变成拼豆格</small></span>
            <Upload size={18} />
          </button>
          <input ref={backgroundInput} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(event) => chooseBackground(event.target.files?.[0] || null)} />
          {backgroundImage && <label className="opacity-row"><span>底图透明度</span><input type="range" min="0.05" max="0.9" step="0.05" value={backgroundOpacity} onChange={(event) => setBackgroundOpacity(Number(event.target.value))} /><strong>{Math.round(backgroundOpacity * 100)}%</strong></label>}
        </fieldset>
        <button className="primary-button generate-button" type="button" onClick={() => onCreateCanvas({ name: canvasName.trim() || '未命名画布', width, height, backgroundImage: backgroundImage || undefined, backgroundOpacity })}>
          <LayoutGrid size={20} /><span>创建画布并开始画</span>
        </button>
      </>}
      <p className="privacy-note"><ShieldCheck size={16} />图片和画布都只保存在本机</p>

      {pendingCrop && <ImageCropModal file={pendingCrop} width={width} height={height} onCancel={() => setPendingCrop(null)} onKeepOriginal={() => finishChoose(sourceFile || pendingCrop, false)} onConfirm={(next) => finishChoose(next, true)} />}
      {pendingBackgroundCrop && <ImageCropModal purpose="background" file={pendingBackgroundCrop} width={width} height={height} onCancel={() => { setPendingBackgroundCrop(null); if (backgroundInput.current) backgroundInput.current.value = ''; }} onKeepOriginal={() => { void finishBackground(pendingBackgroundCrop); }} onConfirm={(next) => { void finishBackground(next); }} />}
    </div>
  );
}
